/**
 * Redux Toolkit slice for streaming animation state.
 *
 * This slice manages the state for streaming messages, including:
 * - Message content (full text and displayed text)
 * - Parser state for token conversion
 * - Animation state and locks
 * - Chunk queues
 *
 * All reducers are pure functions that use Immer for immutability.
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { StreamingState, StreamingMessage, ParserState, ChunkMetadata } from './types';

/**
 * Initial parser state for a new message.
 */
const createInitialParserState = (): ParserState => ({
  buffer: '',
  pendingFact: false,
  factBuffer: '',
  rendered: '',
  // Table parsing fields
  pendingTable: false,
  tableBuffer: '',
  tableBlockId: null,
  backtickBuffer: '',
  tableLang: '',
  tableLangConsumed: false,
});

/**
 * Initial state for the streaming slice.
 */
const initialState: StreamingState = {
  messages: {},
};

/**
 * Streaming slice with reducers for managing streaming message state.
 */
const streamingSlice = createSlice({
  name: 'streaming',
  initialState,
  reducers: {
    /**
     * Called when a new chunk is received from the backend.
     * Appends to fullText (or replaces if isAccumulatedText), enqueues the chunk, and bumps the version.
     *
     * When isAccumulatedText is true (Bot Framework streaming pattern), the chunk contains
     * the full accumulated text so far, not just the delta. We replace fullText and calculate
     * the delta to enqueue for animation.
     */
    messageChunkReceived: (
      state,
      action: PayloadAction<{
        messageId: string;
        chunk: string;
        index: number;
        isAccumulatedText?: boolean;
      }>
    ) => {
      const { messageId, chunk, index, isAccumulatedText = false } = action.payload;

      // Initialize message if it doesn't exist
      if (!state.messages[messageId]) {
        state.messages[messageId] = {
          messageId,
          fullText: '',
          displayedText: '',
          pendingChunks: [],
          parserState: createInitialParserState(),
          isAnimating: false,
          isComplete: false,
          version: 0,
          highestSequence: -1,
          startedAt: Date.now(),
          completedAt: null,
        };
      }

      const message = state.messages[messageId];

      if (isAccumulatedText) {
        // Bot Framework streaming pattern with fire-and-forget: chunks may arrive out of order
        // or be retried, resulting in duplicate sequence numbers.
        // Only process chunks that have a higher sequence than any we've seen so far.
        if (index <= message.highestSequence) {
          // Older or duplicate arrival: we've already processed data up to this sequence, skip it
          return;
        }

        // Guard against cases where the new accumulated text is shorter than what we already have.
        // This can happen due to backend data loss or protocol issues. In that case, update
        // highestSequence but do not enqueue a delta that would be negative/empty.
        if (chunk.length < message.fullText.length) {
          message.fullText = chunk;
          message.highestSequence = index;
          return;
        }

        // Calculate the delta (new content since last update)
        const delta = chunk.slice(message.fullText.length);

        // Replace full text with the accumulated text
        message.fullText = chunk;
        message.highestSequence = index;

        // Only enqueue if there's new content
        if (delta.length > 0) {
          message.pendingChunks.push({
            text: delta,
            index,
            timestamp: Date.now(),
          });
        }
      } else {
        // Legacy pattern: chunk is a delta, append it
        message.fullText += chunk;
        message.highestSequence = Math.max(message.highestSequence, index);

        message.pendingChunks.push({
          text: chunk,
          index,
          timestamp: Date.now(),
        });
      }

      // Bump version
      message.version += 1;
    },

    /**
     * Sets the animation lock for a message.
     * Used to prevent duplicate animations.
     */
    setAnimating: (
      state,
      action: PayloadAction<{
        messageId: string;
        isAnimating: boolean;
      }>
    ) => {
      const { messageId, isAnimating } = action.payload;

      if (state.messages[messageId]) {
        state.messages[messageId].isAnimating = isAnimating;
      }
    },

    /**
     * Updates the displayed text for a message during animation.
     */
    updateDisplayedText: (
      state,
      action: PayloadAction<{
        messageId: string;
        displayedText: string;
      }>
    ) => {
      const { messageId, displayedText } = action.payload;

      if (state.messages[messageId]) {
        state.messages[messageId].displayedText = displayedText;
      }
    },

    /**
     * Updates the parser state for a message.
     * Called during character-by-character animation.
     */
    updateParserState: (
      state,
      action: PayloadAction<{
        messageId: string;
        parserState: ParserState;
      }>
    ) => {
      const { messageId, parserState } = action.payload;

      if (state.messages[messageId]) {
        state.messages[messageId].parserState = parserState;
      }
    },

    /**
     * Removes a processed chunk from the queue.
     */
    removeChunk: (
      state,
      action: PayloadAction<{
        messageId: string;
        chunkIndex: number;
      }>
    ) => {
      const { messageId, chunkIndex } = action.payload;

      if (state.messages[messageId]) {
        const message = state.messages[messageId];
        // Remove chunk matching the given index
        message.pendingChunks = message.pendingChunks.filter(
          (chunk) => chunk.index !== chunkIndex
        );
      }
    },

    /**
     * Marks a message as complete (no more chunks will arrive).
     */
    finalizeMessage: (
      state,
      action: PayloadAction<{
        messageId: string;
      }>
    ) => {
      const { messageId } = action.payload;

      if (state.messages[messageId]) {
        state.messages[messageId].isComplete = true;
        state.messages[messageId].completedAt = Date.now();
      }
    },

    /**
     * Removes a message from the state entirely.
     * Used for cleanup after a message is no longer needed.
     */
    cleanupMessage: (
      state,
      action: PayloadAction<{
        messageId: string;
      }>
    ) => {
      const { messageId } = action.payload;

      delete state.messages[messageId];
    },
  },
});

// Export actions
export const {
  messageChunkReceived,
  setAnimating,
  updateDisplayedText,
  updateParserState,
  removeChunk,
  finalizeMessage,
  cleanupMessage,
} = streamingSlice.actions;

// Export reducer
export default streamingSlice.reducer;

// Export helper for creating initial parser state
export { createInitialParserState };
